import { useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, Upload, Loader2, CheckCircle2, ShieldCheck, X } from "lucide-react";
import api from "@/lib/api";
import { TRANSMISSION_TYPES, SECTOR_OPTIONS } from "@/lib/content";

const empty = {
  nom: "", prenom: "", entreprise: "", email: "", telephone: "",
  pays: "France", region: "", secteur: "", salaries: "", chiffre_affaires: "",
  type_projet: "Transmission totale", message: "",
};

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[var(--gold)]/60 focus:bg-white/[0.05]";

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-wider text-slate-400">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

export function CessionForm() {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.entreprise || !form.email || !form.telephone) {
      toast.error("Merci de renseigner les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      let document_path = null, document_name = null;
      if (file) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        document_path = up.data.path;
        document_name = up.data.filename;
        setUploading(false);
      }
      await api.post("/leads", { ...form, document_path, document_name });
      setDone(true);
      toast.success("Votre dossier a été transmis en toute confidentialité.");
    } catch (err) {
      setUploading(false);
      toast.error("Une erreur est survenue. Merci de réessayer ou d'écrire à contact@losta.group.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-[var(--gold)]/30 bg-[var(--gold)]/[0.04] p-10 sm:p-14 text-center"
        data-testid="cession-success"
      >
        <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
        <h3 className="mt-6 font-serif text-3xl text-white">Message transmis confidentiellement</h3>
        <p className="mt-4 text-slate-300 font-light max-w-lg mx-auto">
          Merci {form.prenom}. Votre demande a bien été reçue. Un premier échange confidentiel et sans engagement vous
          sera proposé dans les meilleurs délais.
        </p>
        <button
          onClick={() => { setForm(empty); setFile(null); setDone(false); }}
          className="btn-ghost mt-8 rounded-full px-6 py-3 text-xs uppercase tracking-wider"
          data-testid="cession-reset"
        >
          Envoyer un autre message
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 card-lux p-6 sm:p-10" data-testid="cession-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Prénom" required><input className={fieldCls} value={form.prenom} onChange={set("prenom")} data-testid="field-prenom" /></Field>
        <Field label="Nom" required><input className={fieldCls} value={form.nom} onChange={set("nom")} data-testid="field-nom" /></Field>
        <Field label="Entreprise" required><input className={fieldCls} value={form.entreprise} onChange={set("entreprise")} data-testid="field-entreprise" /></Field>
        <Field label="Email" required><input type="email" className={fieldCls} value={form.email} onChange={set("email")} data-testid="field-email" /></Field>
        <Field label="Téléphone" required><input className={fieldCls} value={form.telephone} onChange={set("telephone")} data-testid="field-telephone" /></Field>
        <Field label="Pays">
          <select className={fieldCls} value={form.pays} onChange={set("pays")} data-testid="field-pays">
            <option className="bg-[#0F1118]">France</option>
            <option className="bg-[#0F1118]">Suisse</option>
            <option className="bg-[#0F1118]">Autre</option>
          </select>
        </Field>
        <Field label="Région / Canton"><input className={fieldCls} value={form.region} onChange={set("region")} data-testid="field-region" /></Field>
        <Field label="Secteur d'activité">
          <select className={fieldCls} value={form.secteur} onChange={set("secteur")} data-testid="field-secteur">
            <option className="bg-[#0F1118]" value="">Sélectionner…</option>
            {SECTOR_OPTIONS.map((s) => <option key={s} className="bg-[#0F1118]">{s}</option>)}
          </select>
        </Field>
        <Field label="Nombre de salariés"><input className={fieldCls} value={form.salaries} onChange={set("salaries")} data-testid="field-salaries" /></Field>
        <Field label="Chiffre d'affaires approximatif (facultatif)"><input className={fieldCls} value={form.chiffre_affaires} onChange={set("chiffre_affaires")} data-testid="field-ca" /></Field>
      </div>

      <div className="mt-6">
        <Field label="Projet de transmission">
          <div className="flex flex-wrap gap-2.5" data-testid="field-type-projet">
            {TRANSMISSION_TYPES.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setForm((f) => ({ ...f, type_projet: t }))}
                className={`rounded-full border px-4 py-2 text-xs transition-all ${
                  form.type_projet === t ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10" : "border-white/10 text-slate-300 hover:border-white/30"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Parlez-nous brièvement de votre entreprise">
          <textarea rows={4} className={fieldCls} value={form.message} onChange={set("message")} data-testid="field-message" />
        </Field>
      </div>

      <div className="mt-6">
        <span className="mb-2 block text-xs uppercase tracking-wider text-slate-400">Dossier de présentation (facultatif)</span>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)}
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp" data-testid="field-file-input" />
        {!file ? (
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-6 text-sm text-slate-400 hover:border-[var(--gold)]/50 hover:text-gold transition-colors"
            data-testid="field-file-trigger">
            <Upload className="h-4 w-4" /> Joindre un document (PDF, max 15 Mo)
          </button>
        ) : (
          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white">
            <span className="truncate">{file.name}</span>
            <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-white" data-testid="field-file-remove"><X className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
        <p className="text-xs text-slate-400 leading-relaxed">
          <span className="text-slate-200 font-medium">Confidentialité.</span> Les informations transmises sont traitées
          de manière strictement confidentielle et uniquement dans le cadre de l'étude d'une éventuelle opération avec Losta Group.
        </p>
      </div>

      <button type="submit" disabled={submitting}
        className="btn-gold mt-8 flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 text-sm uppercase tracking-wider disabled:opacity-60"
        data-testid="cession-submit">
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {uploading ? "Envoi du document…" : "Transmission…"}</>
        ) : (
          <><Lock className="h-4 w-4" /> Envoyer confidentiellement</>
        )}
      </button>
    </form>
  );
}
